import type { JSX } from 'react'
import { Link, useRouterState } from '@tanstack/react-router'
import {
  Activity,
  BellRing,
  Bot,
  Gauge,
  KeyRound,
  ScrollText,
  Server,
  Settings,
  ShieldCheck,
  X
} from 'lucide-react'
import { BRAND } from '@apiary/shared'
import { cn } from '@/lib/cn'
import { useDashboardSummary } from '@/hooks/queries'
import { useCanManageOrgMembers } from '@/stores/session'
import { IconButton } from '@/ui/primitives/Button'
import { StatusDot } from '@/ui/primitives/StatusDot'

/**
 * Боковая навигация. Ключевое отличие от прошлой версии — сайдбар больше не
 * растягивается по высоте контента: он колонка сетки `AppFrame`, поэтому его
 * высота ровно равна вьюпорту. Список пунктов скроллится внутри себя, а блок
 * состояния runner'а приклеен к НИЗУ САЙДБАРА (`mt-auto`), а не улетает под
 * последнюю карточку страницы.
 *
 * Активный пункт помечен и цветом, и левой планкой, и `aria-current` — цвет тут
 * не единственный носитель смысла.
 */

interface NavItem {
  to: string
  label: string
  icon: JSX.Element
}

const ITEMS: NavItem[] = [
  { to: '/', label: 'Дашборд', icon: <Gauge aria-hidden className="size-4.5" /> },
  { to: '/bots', label: 'Боты', icon: <Bot aria-hidden className="size-4.5" /> },
  { to: '/accounts', label: 'Аккаунты', icon: <KeyRound aria-hidden className="size-4.5" /> },
  { to: '/servers', label: 'Сервера', icon: <Server aria-hidden className="size-4.5" /> },
  { to: '/logs', label: 'Логи', icon: <ScrollText aria-hidden className="size-4.5" /> },
  { to: '/audit', label: 'Аудит', icon: <ShieldCheck aria-hidden className="size-4.5" /> }
]

/**
 * Пункт «Алерты» виден только OWNER/ADMIN: весь раздел закрыт правом
 * `members:manage` (docs/06 §7.1). Показывать OPERATOR'у пункт, который сразу
 * объясняет, что сюда нельзя, — это тупик в навигации, а не информирование.
 */
const ALERTS_ITEM: NavItem = {
  to: '/alerts',
  label: 'Алерты',
  icon: <BellRing aria-hidden className="size-4.5" />
}

export function SideNav({
  className,
  onClose
}: {
  className?: string
  onClose?: () => void
}): JSX.Element {
  const pathname = useRouterState({ select: state => state.location.pathname })
  const canManage = useCanManageOrgMembers()
  const items = canManage ? [...ITEMS, ALERTS_ITEM] : ITEMS

  return (
    <nav
      aria-label="Основная навигация"
      className={cn(
        'h-dvh w-[248px] shrink-0 flex-col gap-2 border-r border-line bg-panel/80 px-3 py-4 backdrop-blur-xl',
        className
      )}
    >
      <div className="flex items-center justify-between gap-2 px-2 pb-2">
        <Link
          to="/"
          className="group flex items-center gap-2.5 rounded-sm"
          aria-label={`${BRAND.name} — на дашборд`}
        >
          <HiveMark />
          <span className="font-display text-md font-bold tracking-[0.14em] text-ink">
            {BRAND.name.toUpperCase()}
          </span>
        </Link>
        {onClose === undefined ? null : (
          <IconButton label="Закрыть меню" onClick={onClose} size="sm">
            <X aria-hidden className="size-4" />
          </IconButton>
        )}
      </div>

      {/* Скролл только у списка пунктов: сам сайдбар не растёт. */}
      <ul className="flex min-h-0 flex-1 flex-col gap-1 overflow-y-auto">
        {items.map(item => (
          <li key={item.to}>
            <NavLink item={item} pathname={pathname} />
          </li>
        ))}
      </ul>

      <div className="mt-2 border-t border-line pt-2">
        <NavLink
          item={{
            to: '/settings',
            label: 'Настройки',
            icon: <Settings aria-hidden className="size-4.5" />
          }}
          pathname={pathname}
        />
      </div>

      <RunnerPulse />
    </nav>
  )
}

function NavLink({ item, pathname }: { item: NavItem; pathname: string }): JSX.Element {
  const active = item.to === '/' ? pathname === '/' : pathname.startsWith(item.to)
  return (
    <Link
      to={item.to}
      aria-current={active ? 'page' : undefined}
      className={cn(
        'press relative flex items-center gap-3 rounded-sm px-3 py-2 text-base',
        active ? 'bg-panel-3 text-ink' : 'text-ink-muted hover:bg-panel-2 hover:text-ink'
      )}
    >
      {/* Планка слева: второй, нецветовой признак активного пункта. */}
      <span
        aria-hidden
        className={cn(
          'absolute top-1/2 left-0 h-5 w-0.5 -translate-y-1/2 rounded-pill bg-honey transition-transform duration-140',
          active ? 'scale-y-100' : 'scale-y-0'
        )}
      />
      <span className={active ? 'text-honey' : 'text-ink-faint'}>{item.icon}</span>
      {item.label}
    </Link>
  )
}

/**
 * Состояние runner'а — всегда на виду, внизу сайдбара. Пока хотя бы один
 * runner офлайн, точка красная: без runner'а панель не может ничего запустить,
 * и это первое, что нужно понять при разборе «почему бот не стартует».
 */
function RunnerPulse(): JSX.Element {
  const summary = useDashboardSummary()
  const runners = summary.data?.runners ?? []
  const online = runners.filter(runner => runner.online).length
  const total = runners.length
  const tone = total === 0 ? 'idle' : online === total ? 'live' : online === 0 ? 'fail' : 'warn'
  const load = runners.find(runner => runner.online)?.load ?? null

  return (
    <div className="mt-3 rounded-md border border-line bg-panel-2/70 px-3 py-2.5">
      <div className="flex items-center justify-between gap-2">
        <span className="flex items-center gap-2 text-xs text-ink-muted">
          <Activity aria-hidden className="size-3.5 text-ink-faint" />
          Runner
        </span>
        <span className="flex items-center gap-2 font-mono text-xs text-ink tabular-nums">
          <StatusDot tone={tone} pulse={tone === 'warn'} />
          {total === 0 ? '—' : `${online}/${total}`}
        </span>
      </div>
      {load === null ? null : (
        <p className="mt-1.5 font-mono text-2xs text-ink-muted tabular-nums">
          {load.bots} ботов · CPU {Math.round(load.cpu * 100)}% · {load.rssMb} МБ
        </p>
      )}
    </div>
  )
}

/** Знак улья: шестиугольник с медовой обводкой. SVG, а не эмодзи. */
function HiveMark(): JSX.Element {
  return (
    <span className="flex size-7 items-center justify-center rounded-sm bg-honey/12">
      <svg viewBox="0 0 24 24" aria-hidden className="size-4">
        <path
          d="M12 2.6 20.1 7v10L12 21.4 3.9 17V7z"
          fill="none"
          stroke="var(--color-honey)"
          strokeWidth="1.8"
          strokeLinejoin="round"
        />
        <path
          d="M12 8.2 16 10.5v4.6L12 17.4 8 15.1v-4.6z"
          fill="var(--color-honey)"
          opacity="0.85"
        />
      </svg>
    </span>
  )
}
